import { HttpClient } from '@angular/common/http';
import { computed, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
	ActivatedRoute,
	ActivatedRouteSnapshot,
	CanActivate,
	Router,
	RouterStateSnapshot,
} from '@angular/router';
import {
	expectedJwtAudience,
	expectedJwtIssuer,
	expectedJwtType,
} from '@app/_core/jwt.constant';
import { JWT } from '@app/_core/models/interfaces/jwt.model';
import {
	addLanguageCode,
	findUrlLanguageCode,
	getLangCode,
	translatePath,
} from '@app/_core/router-utility';
import { TranslateService } from '@app/_modules/translate/translate.service';
import { FeatureFlagService } from '@app/modules/feature-flag/feature-flag.service';
import { FeatureFlags } from '@app/modules/feature-flag/feature-flags.model';
import { BehaviorSubject, Observable, of, ReplaySubject } from 'rxjs';
import {
	catchError,
	distinctUntilChanged,
	filter,
	map,
	switchMap,
	take,
} from 'rxjs/operators';
import { AlertsService } from './AlertsService';
import { CookieService } from './CookieService';
import { SettingsService } from './SettingsService';

export const ehisTokenInitializer = (authService: AuthService) => {
	return () => {
		const token = sessionStorage.getItem('token');
		if (token) {
			authService.getEhisToken(token);
		}
	};
};


export interface UserData {
	iat: number;
	exp: number;
	role: {
		current_role: {
			type: string;
		};
	};
	username: string;
	firstname: string;
	lastname: string;
	usingEhisNames?: boolean;
}

@Injectable({
	providedIn: 'root',
})
export class AuthService implements CanActivate {
	/**
	 * Determines whether if the user is authenticated
	 */
	public isAuthenticated: BehaviorSubject<boolean> =
		new BehaviorSubject<boolean>(false);
	/**
	 * Determines whether the user has EHIS token
	 */
	public hasEhisToken: BehaviorSubject<boolean> = new BehaviorSubject(false);
	public ehisTokenInvalid = new BehaviorSubject(false);
	// Middleman login modal for header and whatever else
	public openLoginModal$ = new ReplaySubject<void>(undefined, 3000);
	public isLoggedInSignal = toSignal(this.isAuthenticated);

	public hasValidEhisAuth = toSignal(
		this.isAuthenticated.pipe(
			filter((val) => val),
			switchMap(() => this.hasEhisToken),
			distinctUntilChanged(),
			filter((val) => val),
		),
	);

	private readonly user = signal<UserData>({} as UserData);

	public userData = computed(() => this.user());

	public expireTime = computed(() => {
		if (!this.userData().exp) {
			return;
		}
		return this.userData().exp * 1000;
	});

	constructor(
		private http: HttpClient,
		private router: Router,
		private route: ActivatedRoute,
		private settings: SettingsService,
		private cookie: CookieService,
		private alertService: AlertsService,
		private translateService: TranslateService,
		private readonly featureFlagService: FeatureFlagService,
	) {
		this.isAuthenticated.next(this.isLoggedIn());
	}

	/**
	 * Logins auth service
	 * @param data - Username, Password
	 * @returns http Observable
	 */
	public login(data: any) {
		return this.http
			.post(`${this.settings.url}/api/v1/token?_format=json`, data)
			.pipe(
				catchError(() => {
					return of({});
				}),
				map((response: any) => {
					if (response?.['token']) {
						sessionStorage.setItem('token', response['token']);
						this.setUser(this.decodeToken(response.token));
						this.testNewJWT(response['token']);
					} else {
						sessionStorage.removeItem('token');
						sessionStorage.removeItem('ehisToken');
						sessionStorage.removeItem('redirectUrl');
					}
					this.isAuthenticated.next(this.isLoggedIn());
					return response;
				}),
			);
	}

	/**
	 * Tests new JSON web token
	 * @param token - jwt token
	 */
	public testNewJWT(token) {
		const data = {
			jwt: token,
		};
		this.http
			.post(`${this.settings.ehisUrl}/users/v1/haridusportaal/jwt`, data)
			.subscribe({
				next: (response: any) => {
					if (response.jwt) {
						const isValidJwt = this.verifyJwt(this.decodeToken(response.jwt));
						if (isValidJwt) {
							sessionStorage.setItem('ehisToken', response.jwt);
							this.saveEhisTokenInCookie(response.jwt);
							this.hasEhisToken.next(true);
							this.isAuthenticated.next(true);
						} else {
							this.ehisTokenInvalid.next(true);
							this.alertInvalidJwt();
						}
					}
					this.useRedirectUrl(true);
				},
				error: (err) => {
					this.useRedirectUrl();
				},
			});
	}

	/**
	 * Gets ehis token
	 * Used to refresh users token
	 * @param token - JWT token
	 */
	public getEhisToken(token) {
		this.http
			.post(`${this.settings.ehisUrl}/users/v1/haridusportaal/jwt`, {
				jwt: token,
			})
			.pipe(take(1))
			.subscribe((res: any) => {
				if (res.jwt) {
					const isValidJwt = this.verifyJwt(this.decodeToken(res.jwt));
					if (isValidJwt) {
						sessionStorage.setItem('ehisToken', res.jwt);
						this.saveEhisTokenInCookie(res.jwt);
						this.hasEhisToken.next(true);
					} else {
						this.ehisTokenInvalid.next(true);
						this.alertInvalidJwt();
					}
				}
			});
	}

	public getAnonToken() {
		this.http
			.get(`${this.settings.ehisUrl}/users/v1/anonymous/jwt`, {
				responseType: 'text',
			})
			.subscribe((res: string) => {
				sessionStorage.setItem('ehisToken', res);
			});
	}

	/**
	 * Sets ehis token in cookies
	 * @param token - JWT token
	 */
	public saveEhisTokenInCookie(token) {
		this.cookie.set('ehisToken', token, 0);
	}

	/**
	 * Deletes ehis token from cookies
	 */
	public deleteEhisTokenFromCookie() {
		this.cookie.remove('ehisToken');
	}

	/**
	 * Logs user out of the page and navigates the user to homepage
	 */
	public logout() {
		sessionStorage.removeItem('token');
		sessionStorage.removeItem('redirectUrl');
		sessionStorage.removeItem('ehisToken');
		this.deleteEhisTokenFromCookie();

		this.isAuthenticated.next(false);
		this.hasEhisToken.next(false);
		this.ehisTokenInvalid.next(false);
		this.user.set({} as UserData);
		this.router.navigateByUrl('/');
	}

	/**
	 * Determines whether the user is logged in
	 * @returns boolean
	 */
	public isLoggedIn() {
		if (!sessionStorage.getItem('token')) {
			if (this.isAuthenticated.getValue()) {
				this.isAuthenticated.next(false);
			}
			return false;
		}
		if (this.isTokenExpired()) {
			sessionStorage.removeItem('token');
			sessionStorage.removeItem('ehisToken');
			sessionStorage.removeItem('redirectUrl');
			this.deleteEhisTokenFromCookie();

			if (this.isAuthenticated.getValue()) {
				this.isAuthenticated.next(false);
				this.hasEhisToken.next(false);
			}
			return false;
		}
		this.refreshUser();
		return true;
	}

	/**
	 * Refreshs user
	 * Updates users storage
	 * @param [newToken] - JWT token
	 */
	public refreshUser(newToken: any = false) {
		// TODO: refactor this somehow? currently gets called an insane amount of times
		if (newToken) {
			sessionStorage.setItem('token', newToken);
			this.getEhisToken(newToken);
		}
		const token = sessionStorage.getItem('token');
		if (sessionStorage.getItem('ehisToken')) {
			this.hasEhisToken.next(true);
		}
		this.setUser(this.decodeToken(token));
	}

	/**
	 * Decodes token
	 * @param token - JWT token
	 * @returns - decoded token
	 */
	public decodeToken(token) {
		const payload = JSON.parse(decodeURIComponent(atob(token.split('.')[1])));
		return payload;
	}

	/**
	 * Auth guard
	 * @returns activate
	 */
	public canActivate(
		next: ActivatedRouteSnapshot,
		state: RouterStateSnapshot,
	): Observable<boolean> | Promise<boolean> | boolean {
		try {
			const url = state.url.startsWith('/') ? state.url.slice(1) : state.url;
			const expectedLangCode = findUrlLanguageCode(url);
			if (expectedLangCode && expectedLangCode !== getLangCode()) {
				const newUrl = addLanguageCode(url, expectedLangCode);
				// Router could end in a loop due to url slice
				if (newUrl.endsWith(url) && `/${url}` === newUrl) {
					return true;
				}
				this.router.navigateByUrl(newUrl);
				return false;
			}
		} catch (e) {
			console.error('Error during route parsing/repair', e);
		}
		if (!this.isLoggedIn()) {
			this.router.navigate(['/auth'], {
				queryParams: { redirect: decodeURIComponent(state.url) },
			});
		} else if (!sessionStorage.getItem('ehisToken')) {
			this.getEhisToken(sessionStorage.getItem('token'));
		}
		return true;
	}

	public useRedirectUrl(checkSessionStorage?: boolean) {
		let redirectUrl = this.route.snapshot.queryParamMap.get('redirect');
		if (checkSessionStorage && !redirectUrl) {
			redirectUrl = sessionStorage.getItem('redirectUrl');
		}
		const defaultRedirectUrl = this.featureFlagService.isFlagDisabled(
			FeatureFlags.DASHBOARD_DISABLED,
		)
			? translatePath('/dashboard')
			: addLanguageCode('', this.settings.currentAppLanguage);
		this.router.navigateByUrl(redirectUrl || defaultRedirectUrl, {
			replaceUrl: !!redirectUrl,
		});
	}

	private isTokenExpired() {
		const token = sessionStorage.getItem('token');
		const tokenPayload = this.decodeToken(token);

		return Date.now() >= tokenPayload.exp * 1000;
	}

	public triggerLoginModal(redirectUrl?: string) {
		// Set the path to be redirected to after login
		if (redirectUrl) {
			globalThis?.sessionStorage?.setItem('redirectUrl', redirectUrl);
		}
		this.openLoginModal$.next();
	}

	public setUser(data: Partial<UserData>) {
		/**
		 * Skip replacing names if we're using new names from EHIS
		 */
		if (this.user()?.usingEhisNames) {
			const { firstname, lastname, ...restOfData } = data;
			this.user.update((prev) => ({ ...prev, ...restOfData }));
		} else {
			this.user.update((prev) => ({ ...prev, ...data }));
		}
	}

	private verifyJwt(jwt: JWT): boolean {
		const expectedValues = {
			'token-type': expectedJwtType,
			aud: expectedJwtAudience,
			iss: expectedJwtIssuer,
		} as Partial<JWT>;
		// Return early if any of the keys are missing
		const hasRequiredKeys = Object.keys(expectedValues).reduce((acc, key) => {
			return acc && key in jwt;
		}, true);
		if (!hasRequiredKeys) {
			return false;
		}

		const hasExpectedValues = Object.entries(jwt ?? {})?.reduce(
			(acc, [key, val]) => {
				// Compare keys that have an expected value
				if (key in expectedValues && expectedValues?.[key] !== val) {
					return false;
				}
				return acc;
			},
			true,
		);
		// Make sure the expiration date is valid
		const currentTime = new Date().getTime();
		const isValidExpiration = currentTime < jwt.exp * 1000;
		return hasExpectedValues && isValidExpiration;
	}

	private alertInvalidJwt() {
		this.alertService.error(
			this.translateService.get('errors.ehis_auth_failed'),
			'global',
			'auth-errors',
			true,
		);
	}

}
